import React, { FC, useCallback, useEffect, useState } from 'react';
import { ICustomizerProps } from '../../pages';
import { AnyObject, APP_STYLES, DEFAULT_STYLES, urlToParams } from '../../utils/basic';
import { bsLog } from '../../utils/bestSeller';

export interface IParams {
  pathParams: { pathPrefix?: string; [key: string]: any };
  queryParams: AnyObject;
  otherQueryParams?: AnyObject;
}

export interface IUrl {
  pathString: string;
  queryString: string;
  ring_url: string;
}

export interface ICustomizerHOCProps {
  match: any;
  location: any;
  history: any;
  type: number;
  ring_url?: string;
  otherQueryParams?: AnyObject;
}

const RingCustomizerHOC = (Component: FC<ICustomizerProps>) => {
  return function HOC(props: ICustomizerHOCProps) {
    const { match, location, type, ring_url, otherQueryParams } = props;

    const [params, setParams] = useState<IParams>({
      pathParams: {},
      queryParams: {}
    });

    const [url, setUrl] = useState<IUrl>({
      pathString: '',
      queryString: '',
      ring_url: ''
    });

    useEffect(() => {
      function handleResize() {
        const vh = window.innerHeight;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      const {
        pathParams: { pathPrefix }
      } = params;
      if (pathPrefix) {
        const styles = APP_STYLES[pathPrefix] || DEFAULT_STYLES;

        document.documentElement.style.setProperty('--brand-color', styles.brandColor);

        document.documentElement.style.setProperty('--primary-bg-color', styles.primaryBgColor);
        document.documentElement.style.setProperty('--secondary-bg-color', styles.secondaryBgColor);

        document.documentElement.style.setProperty('--primary-color', styles.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', styles.secondaryColor);
        document.documentElement.style.setProperty('--tertiary-color', styles.tertiaryColor);

        document.documentElement.style.setProperty('--primary-font-family', styles.primaryFont);
        document.documentElement.style.setProperty('--secondary-font-family', styles.secondaryFont);

        document.documentElement.style.setProperty('--brand-logo', styles.brandlogo);
        document.documentElement.style.setProperty('--brand-logo-secondary', styles.brandlogoSecondary);

        document.documentElement.style.setProperty('--primary-border-color', styles.primaryBorderColor);
        document.documentElement.style.setProperty('--secondary-border-color', styles.secondaryBorderColor);

        document.documentElement.style.setProperty('--primary-border-radius', styles.primaryBorderRadius);
        document.documentElement.style.setProperty('--secondary-border-radius', styles.secondaryBorderRadius);
        document.documentElement.style.setProperty('--tertiary-border-radius', styles.tertiaryBorderRadius);

        document.documentElement.style.setProperty('--rc-box-tab-case', styles.rcBoxTabCase);

        document.documentElement.style.setProperty('--sidebar-order', styles.sidebarOrder);
      }
    }, [params.pathParams.pathPrefix]); // if params, it will call everytime when option choose

    useEffect(() => {
      const url = ring_url || `${location.pathname}${location.search}`;
      if (url) {
        const { pathParams, queryParams: searchParams } = urlToParams(url);
        const { product_id, bestSeller, ...queryParams } = searchParams;
        bsLog('params rebuilt from url', {
          fromUrl: bestSeller,
          fromProp: otherQueryParams?.bestSeller,
          applied: bestSeller || otherQueryParams?.bestSeller
        });
        setParams({
          pathParams,
          queryParams,
          otherQueryParams: {
            // Params parsed out of the url win over the ones handed down as a
            // prop: the url is what the app itself just wrote (see
            // `handleBestSeller`), while the prop is a copy held in a parent's
            // state that can still be a commit behind. Both are spread
            // conditionally, so a param the url does not carry leaves the
            // prop's value in place.
            ...otherQueryParams,
            ...(product_id ? { product_id } : {}),
            ...(bestSeller ? { bestSeller } : {})
          }
        });
        if (type === 1) {
          const { profile, width, ring_materials } = pathParams;
          const pathString = `/rings/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        } else if (type === 2) {
          const { styles, ring_materials, color_arrangements, stone_quantities, variants } = pathParams;
          const pathString = `/women/rings/styles/${styles}/ring_materials/${ring_materials}/color_arrangements/${color_arrangements}/stone_quantities/${stone_quantities}/variants/${variants}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        } else if (type === 3) {
          const { ring_type, ring_materials } = pathParams;
          const pathString = `/rings/${ring_type}/ring_materials/${ring_materials}`;
          const queryString = new URLSearchParams(queryParams).toString();
          setUrl({
            pathString: pathString,
            queryString,
            ring_url: `${pathString}?${queryString}`
          });
        }
      }
    }, [ring_url, type, location, match, otherQueryParams]);

    return <Component {...props} params={params} setParams={setParams} url={url} isQueryHandling={!!ring_url} />;
  };
};

export default RingCustomizerHOC;
